import React, {useState, useEffect, useRef} from 'react';
import {Link, useLocation} from 'react-router-dom';
import clsx from 'clsx';
import {Twirl as Hamburger} from 'hamburger-react';

import {routes} from '../../constants/routes';
import {LogoComponent} from '../Logo';
import styles from './styles.module.scss';
import {
  Star,
  Arrow,
  Tutorial,
  Status,
  Career,
  Bounty,
  Changelog,
  Help,
  DiscordNav,
  TwitterNav,
} from '../../assets';
import {socialLinks} from '../../constants/links';
import {useUser} from '../../hooks/useAuth';

const nav = [
  {
    id: '1',
    title: 'Home',
    path: routes.home,
  },
  {
    id: '2',
    title: 'Features',
    path: routes.features,
  },
  {
    id: '3',
    title: 'Premium',
    path: routes.premium,
    icon: <Star />,
    mod: 'gold',
  },
  {
    id: '4',
    title: 'Community',
    icon: <Arrow />,
    links: [
      {
        id: '1',
        title: 'Discord',
        icon: <DiscordNav />,
        href: socialLinks.discord,
      },
      {
        id: '2',
        title: 'Twitter',
        icon: <TwitterNav />,
        href: socialLinks.twitter,
      },
    ],
  },
  {
    id: '5',
    title: 'Resources',
    icon: <Arrow />,
    links: [
      {
        id: '1',
        title: 'Tutorial',
        icon: <Tutorial />,
        href: 'https://www.youtube.com/watch?v=4YQ4svkETS0',
      },
      {
        id: '2',
        title: 'Status',
        icon: <Status />,
        path: routes.notification,
      },
      {
        id: '3',
        title: 'Careers',
        icon: <Career />,
        path: routes.notification,
      },
      {
        id: '4',
        title: 'Bug Bounty',
        icon: <Bounty />,
        path: routes.notification,
      },
      {
        id: '5',
        title: 'Changelog',
        icon: <Changelog />,
        path: routes.changelog,
      },
      {
        id: '6',
        title: 'Help',
        icon: <Help />,
        path: routes.notification,
      },
    ],
  },
  {
    id: '6',
    title: 'Login',
    secondTitle: 'Servers',
    path: routes.auth,
    secondPath: routes.chooseServer,
    mod: 'login',
  },
];

export const Navigation = () => {
  const {pathname} = useLocation();
  const [isMobMenu, setIsMobMenu] = useState(false);
  const {isAuth} = useUser();

  const handleMobMenu = () => {
    setIsMobMenu((prev) => !prev);
  };

  const handleMobMenuClose = () => {
    setIsMobMenu(false);
  };

  return (
    <div className={styles.navigation}>
      <div className={styles.content}>
        <div className={styles.logo}>
          <LogoComponent primary />
        </div>
        <nav className={styles.nav}>
          {nav.map((item) => {
            if (item.links) {
              return (
                <MenuItem
                  item={item}
                  key={item.id}
                  pathname={pathname}
                  onClick={handleMobMenuClose}
                />
              );
            }
            return (
              <NavLink
                item={item}
                key={item.id}
                pathname={pathname}
                onClick={handleMobMenuClose}
                isAuth={isAuth}
              />
            );
          })}
        </nav>

        <div className={styles.navMob}>
          <div className={styles.right}>
            {<NavLink item={nav[5]} pathname={pathname} isAuth={isAuth} />}
            <div className={styles.burger} onClick={handleMobMenu}>
              <Hamburger toggled={isMobMenu} aria-label="Burger button" size={18} />
            </div>
          </div>

          {isMobMenu && (
            <div className={styles.mobMenu}>
              {nav.slice(0, nav.length - 1).map((item, i, arr) => {
                if (item.links) {
                  return (
                    <MenuItem
                      item={item}
                      key={item.id}
                      pathname={pathname}
                      last={i === arr.length - 1}
                      onClick={handleMobMenuClose}
                    />
                  );
                }
                return (
                  <NavLink
                    item={item}
                    key={item.id}
                    pathname={pathname}
                    last={i === arr.length - 1}
                    onClick={handleMobMenuClose}
                    isAuth={isAuth}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MenuItem = ({item, pathname, last, onClick}) => {
  const [showBlock, setShowBlock] = useState(false);
  const timeoutRef = useRef(null);

  const handleClick = () => {
    setShowBlock((prev) => !prev);
  };

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setShowBlock(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setShowBlock(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div
      key={item.id}
      className={clsx(styles.menuItem, {
        [styles.last]: last,
      })}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}>
      <div
        className={clsx(styles.menuItemTitle, {
          [styles.active]: showBlock,
        })}>
        {item.title}
        {item.icon && <div>{item.icon}</div>}
      </div>
      <div
        className={clsx(styles.menuItemLinks, {
          [styles.active]: showBlock,
        })}>
        <div className={styles.menuItemLinksWrapper}>
          {item.links.map((link) => {
            if (link.href) {
              return (
                <a
                  onClick={onClick}
                  href={link.href}
                  key={link.id}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={clsx(styles.menuItemLink, styles.link)}>
                  {link.icon && <div>{link.icon}</div>}
                  {link.title}
                </a>
              );
            }

            return (
              <Link
                onClick={onClick}
                to={link.path}
                key={link.id}
                className={clsx(styles.menuItemLink, styles.link, {
                  [styles.active]: pathname === link.path,
                })}>
                {link.icon && <div>{link.icon}</div>}
                {link.title}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const NavLink = ({item, pathname, last, onClick, isAuth}) => {
  return (
    <Link
      onClick={onClick}
      to={isAuth && item.secondPath ? item.secondPath : item.path}
      key={item.id}
      className={clsx(styles.navItem, item.mod && styles[item.mod], {
        [styles.active]: pathname === item.path,
        [styles.last]: last,
      })}>
      {isAuth && item.secondTitle ? item.secondTitle : item.title}
      {item.icon && <div className={styles.icon}>{item.icon}</div>}
    </Link>
  );
};
